import styled from 'styled-components';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { spacing } from 'ui/theme';
import { selectUserContext } from 'core/userContext';
import { pxToRem } from 'utils/convert';
import { ORG_PERMISSIONS } from 'utils/permissions';
import {
  OrganizationMembersQueryContextProvider,
  useOrganizationMembersQueryContext,
} from '../../contexts/OrganizationMembersQueryContext';
import { OrganizationModalsContextProvider } from '../../contexts/OrganizationModalsContextProvider';
import { MemberDetailsPageHeader } from './MemberDetailsPageHeader';
import { NonEditableDetails } from './NonEditableDetails';
import { ProjectMembership } from './ProjectMembership';
import { Banners } from './Banners';
import { Modals } from './Modals';
import { MemberNotFound } from './MemberNotFound';

const Main = styled.main`
  padding: ${spacing.plusHalf} 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr ${pxToRem(300)};
  align-items: start;
  column-gap: ${spacing.base};
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: ${spacing.base};
    & > *:first-child {
      order: 2;
    }
    & > *:nth-child(2) {
      order: 1;
    }
  }
`;

export const BaseMemberDetailsPageModernized = () => {
  const { isLoading, membersById } = useOrganizationMembersQueryContext();
  const { userId = '' } = useParams<{ userId: string }>();
  const { rbacPermissions } = useSelector(selectUserContext);
  const hasManageMembersPermission = rbacPermissions?.includes(ORG_PERMISSIONS.ManageMembers);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!hasManageMembersPermission || isLoading) return null;

  const member = membersById[userId as keyof typeof membersById];

  if (!member) return <MemberNotFound />;

  return (
    <div>
      <MemberDetailsPageHeader member={member} />
      <Main>
        <Banners member={member} />
        <Grid>
          <ProjectMembership member={member} />
          <NonEditableDetails member={member} />
        </Grid>
      </Main>
      <Modals />
    </div>
  );
};

export const MemberDetailsPageModernized = () => (
  <OrganizationMembersQueryContextProvider>
    <OrganizationModalsContextProvider>
      <BaseMemberDetailsPageModernized />
    </OrganizationModalsContextProvider>
  </OrganizationMembersQueryContextProvider>
);
